import React, { useEffect, useMemo, useRef, useState } from "react";
import Search from "../../assets/icons/Search.svg";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import {
  globalBibleSearchAction,
  globalCategoriesSearchAction,
  globalCurriculumSearchAction,
  globalJournalSearchAction,
  globalPromiseSearchAction,
  globalTestimonySearchAction,
  globalVideoSearchAction,
} from "../../store/Home/HomeAction";
import {
  setGlobalBibleSearchResults,
  setGlobalCategoriesSearchResults,
  setGlobalCurriculumSearchResults,
  setGlobalJournalSearchResults,
  setGlobalPromiseSearchResults,
  setGlobalTestimonySearchResults,
  setGlobalVideoSearchResults,
} from "../../store/Common/CommonAction";
import LoaderSpinner from "./LoaderSpinner";
import { debounce } from "../../utils/Utils";
import { LuSearch } from "react-icons/lu";

const SearchBar = ({ placeholder, name, className }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const containerRef = useRef();

  // Selectors
  const {
    globalBibleSearchResults,
    globalVideoSearchResults,
    globalTestimonySearchResults,
    globalPromiseSearchResults,
    globalJournalSearchResults,
    globalCurriculumSearchResults,
    globalCategoriesSearchResults,
  } = useSelector((state) => state.common);

  // State
  const [searchText, setSearchText] = useState("");
  const [searchActive, setSearchActive] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [loadingStates, setLoadingStates] = useState({
    bible: false,
    video: false,
    testimony: false,
    promise: false,
    journal: false,
    curriculum: false,
    categories: false,
  });

  // Tab configuration
  const tabs = [
    { label: "All", value: "all" },
    { label: "Bible", value: "bible" },
    { label: "Videos", value: "videos" },
    { label: "Categories", value: "categories" },
    { label: "Curriculum", value: "curriculum" },
    { label: "Testimony", value: "testimony" },
    { label: "Promise", value: "promise" },
    { label: "Journal", value: "journal" },
  ];

  // Search results configuration
  const searchConfig = {
    bible: {
      title: "Bible",
      results: globalBibleSearchResults,
      loading: loadingStates.bible,
      navigate: (item) =>
        navigate(`/chapter/ENGESV/${item?.book_id}/${item?.chapter}`),
      renderItem: (item) => (
        <>
          <p className="text-white">{item?.verse_text}</p>
          <div className="flex w-full items-center justify-between text-sm">
            <p className="font-medium text-greyish">
              {item?.book_name} {item?.chapter}:{item?.verse_start}
            </p>
          </div>
        </>
      ),
    },
    videos: {
      title: "Videos",
      results: globalVideoSearchResults,
      loading: loadingStates.video,
      navigate: (item) => navigate(`/videoInfo/${item?._id}`),
      renderItem: (item) => {
        const thumbnail =
          item?.tumbnailurls?.find((i) => i.type_of_url === "1")?.url_link ||
          "";
        return (
          <>
            <img
              src={thumbnail}
              className="aspect-video h-20 rounded-xl object-cover object-top"
              alt=""
            />
            <div>
              <p className="line-clamp-1 overflow-ellipsis text-white">
                {item?.video_title}
              </p>
              <p className="line-clamp-2 overflow-ellipsis">
                {item?.video_description}
              </p>
            </div>
          </>
        );
      },
    },
    categories: {
      title: "Categories",
      results: globalCategoriesSearchResults,
      loading: loadingStates.categories,
      navigate: (item) => navigate(`/categoryInfo/${item?._id}`),
      renderItem: (item) => {
        const thumbnail =
          item?.icon_images?.find((i) => i.type_of_url === "1")?.url_link || "";
        return (
          <>
            <img
              src={thumbnail}
              className="aspect-video h-20 rounded-xl object-cover object-top"
              alt=""
            />
            <div>
              <p className="line-clamp-1 overflow-ellipsis text-white">
                {item?.category_name}
              </p>
              <p className="line-clamp-2 overflow-ellipsis">
                {item?.category_description}
              </p>
            </div>
          </>
        );
      },
    },
    curriculum: {
      title: "Curriculums",
      results: globalCurriculumSearchResults,
      loading: loadingStates.curriculum,
      navigate: (item) => navigate(`/curriculum-details/${item?._id}`),
      renderItem: (item) => {
        const thumbnail =
          item?.bg_Images?.find((i) => i.type_of_url === "1")?.url_link || "";
        return (
          <>
            <img
              src={thumbnail}
              className="aspect-video h-20 rounded-xl object-cover object-top"
              alt=""
            />
            <div>
              <p className="line-clamp-1 overflow-ellipsis text-white">
                {item?.curriculum_name}
              </p>
              <p className="line-clamp-2 overflow-ellipsis">
                {item?.curriculum_description}
              </p>
            </div>
          </>
        );
      },
    },
    testimony: {
      title: "Testimonies",
      results: globalTestimonySearchResults,
      loading: loadingStates.testimony,
      navigate: (item) => navigate(`/testimonialInfo/${item?._id}`),
      renderItem: (item) => {
        const thumbnail =
          item?.testomonial_rec_tumbnailUrl ??
          "https://twg-assets-dev.s3.amazonaws.com/todaywithgodemptylandscape-m.jpg";
        return (
          <>
            <img
              src={thumbnail}
              className={`aspect-video h-20 rounded-xl object-cover ${item?.testomonial_rec_tumbnailUrl ? "object-top" : "object-center"}`}
              alt=""
            />
            <div>
              <p className="line-clamp-1 overflow-ellipsis text-white">
                {item?.testimonial_name}
              </p>
              <p className="line-clamp-2 overflow-ellipsis">
                {item?.testimonial_description}
              </p>
            </div>
          </>
        );
      },
    },
    promise: {
      title: "Promises",
      results: globalPromiseSearchResults,
      loading: loadingStates.promise,
      navigate: (item) => navigate(`/devotions/${item?._id}`),
      renderItem: (item) => {
        const thumbnail =
          item?.bg_Images?.find((i) => i.type_of_url === "1")?.url_link || "";
        return (
          <>
            <img
              src={thumbnail}
              className="aspect-video h-20 rounded-xl object-cover object-top"
              alt=""
            />
            <div>
              <p className="line-clamp-1 overflow-ellipsis text-white">
                {item?.twg_title}
              </p>
              <p className="line-clamp-2 overflow-ellipsis">
                {item?.read_gods_word}
              </p>
            </div>
          </>
        );
      },
    },
    journal: {
      title: "Journals",
      results: globalJournalSearchResults,
      loading: loadingStates.journal,
      navigate: (item) => navigate(`/journals/${item?._id}`),
      renderItem: (item) => {
        const thumbnail =
          item?.bg_Images?.find((i) => i.type_of_url === "1")?.url_link || "";
        return (
          <>
            <img
              src={thumbnail}
              className="aspect-video h-20 rounded-xl object-cover object-top"
              alt=""
            />
            <div>
              <p className="line-clamp-1 overflow-ellipsis text-white">
                {item?.journal_title}
              </p>
              <p className="line-clamp-2 overflow-ellipsis">
                {item?.journal_description}
              </p>
            </div>
          </>
        );
      },
    },
  };

  // Debounced search actions
  const searchActions = useMemo(
    () => ({
      bible: (params) => dispatch(globalBibleSearchAction(params)),
      video: (params) => dispatch(globalVideoSearchAction(params)),
      testimony: (params) => dispatch(globalTestimonySearchAction(params)),
      promise: (params) => dispatch(globalPromiseSearchAction(params)),
      journal: (params) => dispatch(globalJournalSearchAction(params)),
      curriculum: (params) => dispatch(globalCurriculumSearchAction(params)),
      categories: (params) => dispatch(globalCategoriesSearchAction(params)),
    }),
    []
  );

  // Result setters
  const resultSetters = {
    bible: setGlobalBibleSearchResults,
    video: setGlobalVideoSearchResults,
    testimony: setGlobalTestimonySearchResults,
    promise: setGlobalPromiseSearchResults,
    journal: setGlobalJournalSearchResults,
    curriculum: setGlobalCurriculumSearchResults,
    categories: setGlobalCategoriesSearchResults,
  };

  // Search in store
  const searchInStore = (text) => {
    if (text) {
      // Set all loading states to true
      setLoadingStates({
        bible: true,
        video: true,
        testimony: true,
        promise: true,
        journal: true,
        curriculum: true,
        categories: true,
      });

      // Dispatch search actions for each type (debounced)
      Object.entries(searchActions).forEach(([key, action]) => {
        action({
          apiPayloadRequest: { question: text },
          callback: (res) => {
            dispatch(resultSetters[key](res?.data ?? []));
            setLoadingStates((prev) => ({ ...prev, [key]: false }));
          },
        });
      });
    }
  };

  const handleChange = (e) => {
    setSearchText(e.target.value);
  };

  // Click outside handler
  useEffect(() => {
    const eventHandler = (e) => {
      if (containerRef.current && !containerRef.current?.contains(e.target)) {
        setSearchActive(false);
      }
    };
    document.addEventListener("mouseup", eventHandler);
    return () => document.removeEventListener("mouseup", eventHandler);
  }, []);

  // Helper components
  const NoResults = () => (
    <p className="py-8 text-center">No Results found ..</p>
  );
  const SearchPrompt = () => (
    <p className="py-8 text-center">Search in Today With God</p>
  );

  // Result item component
  const ResultItem = ({ item, onClick, children, isGrid = false }) => (
    <div
      className={`cursor-pointer rounded-md px-2 py-2 hover:bg-greyishV4 ${isGrid ? "flex gap-4" : ""}`}
      onClick={onClick}
    >
      {children}
    </div>
  );

  // Section component with loading state
  const Section = ({ title, items, loading, config, limit = 0 }) => {
    if (!searchText) return null;
    if (!items?.length) return null;

    const itemsToShow = limit > 0 ? items.slice(0, limit) : items;

    return (
      <div>
        <h6 className="font-medium text-yellowish">{title}</h6>
        {loading ? (
          <ul className="flex flex-col gap-2">
            <div className="flex w-full animate-pulse flex-col gap-2 rounded-md bg-greyishV4 p-2">
              <div className="h-4 w-6/12 animate-pulse rounded-md bg-greyishV5"></div>
              <div className="h-4 w-2/12 animate-pulse rounded-md bg-greyishV5"></div>
            </div>
            <div className="flex w-full animate-pulse flex-col gap-2 rounded-md bg-greyishV4 p-2">
              <div className="h-4 w-6/12 animate-pulse rounded-md bg-greyishV5"></div>
              <div className="h-4 w-2/12 animate-pulse rounded-md bg-greyishV5"></div>
            </div>
          </ul>
        ) : (
          itemsToShow.map((item, index) => (
            <ResultItem
              key={`${title}-item-${index}`}
              item={item}
              onClick={() => config.navigate(item)}
              isGrid={title !== "Bible"}
            >
              {config.renderItem(item)}
            </ResultItem>
          ))
        )}
      </div>
    );
  };

  // All tab content with loading state
  const renderAllTab = () => {
    const hasNoResults =
      searchText &&
      Object.values(searchConfig).every((config) => !config.results?.length);

    if (hasNoResults) return <NoResults />;
    if (!searchText) return <SearchPrompt />;

    return (
      <div className="flex flex-col gap-4">
        {Object.entries(searchConfig).map(([key, config]) => (
          <Section
            key={key}
            title={config.title}
            items={config.results}
            loading={config.loading}
            config={config}
            limit={2}
          />
        ))}
      </div>
    );
  };

  // Single tab content with loading state
  const renderTabContent = (tabKey) => {
    const config = searchConfig[tabKey];
    if (!config) return null;

    if (!searchText) return <SearchPrompt />;
    if (config.loading) {
      return (
        <div className="flex justify-center py-8">
          <LoaderSpinner />
        </div>
      );
    }
    if (!config.results?.length) return <NoResults />;

    return (
      <Section
        title={config.title}
        items={config.results}
        loading={config.loading}
        config={config}
      />
    );
  };

  return (
    <div className={`w-full px-[10%] ${className ?? ""}`} ref={containerRef}>
      <div
        className={`relative flex w-full justify-between gap-3 rounded-[12px] border border-gray-400 border-opacity-5 bg-[#131313] px-4 py-[13px] font-[450] ${
          searchActive && searchText ? "rounded-b-none border" : ""
        }`}
      >
        <input
          className="w-full bg-black bg-opacity-0 text-[15px] text-white placeholder-white placeholder-opacity-20 focus:outline-none"
          name={name}
          onFocus={() => setSearchActive(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              searchInStore(searchText);
            }
          }}
          value={searchText}
          placeholder={placeholder}
          onChange={handleChange}
        />

        <button
          className="flex aspect-square items-center justify-center rounded-full bg-white p-1 text-xl text-blackish"
          onClick={() => searchInStore(searchText)}
        >
          <LuSearch className="" />
        </button>
        <div
          className={`no-scrollbar bottom-0 left-0 z-[99999] max-h-[50vh] w-full translate-y-full overflow-y-auto border border-gray-400 border-opacity-5 bg-[#131313] px-4 py-2 text-white/40 ${
            searchActive ? "absolute" : "hidden"
          }`}
        >
          <Swiper
            slidesPerView="auto"
            freeMode={true}
            spaceBetween={4}
            className="w-full bg-[#131313] !pb-4"
            grabCursor={true} // Shows grab cursor when hovered
            mousewheel={{ forceToAxis: true }} // Enables mousewheel scrolling
          >
            {tabs.map((tab, index) => (
              <SwiperSlide
                key={`search-tab-${index}`}
                className="select-none"
                style={{ width: "auto" }}
                onClick={(e) => {
                  // Prevent click events from propagating to the button when swiping
                  if (e.target.tagName === "BUTTON") {
                    setActiveTab(index);
                  }
                }}
              >
                <button
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm ${
                    activeTab === index
                      ? "bg-white text-black"
                      : "bg-greyishV2 text-white"
                  }`}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent event from reaching SwiperSlide
                    setActiveTab(index);
                  }}
                >
                  {tab.label}
                </button>
              </SwiperSlide>
            ))}
          </Swiper>
          <div className="no-scrollbar overflow-y-scroll">
            {activeTab === 0
              ? renderAllTab()
              : renderTabContent(tabs[activeTab].value)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchBar;
